<template>
  <v-layout row wrap>
    <v-flex xs6>
      <v-form class="px-2">
        <!-- Rule For -->
        <v-flex xs4>
          <v-select v-model="ruleSetInfo.ruleSetFor" :label="messages.label.FOR" :disabled="isEditMode"
            item-text="title" item-value="value" :items="ruleSetForList" @change="updateAddRulesFilters">
          </v-select>
        </v-flex>
        <template v-if="ruleSetInfo.ruleSetFor === 'account'">
          <div v-if="IS_POLARIS">
            <!-- Provider Group -->
            <v-flex sm4>
              <v-autocomplete v-model="selectedProviderGroup" :label="messages.label.PROVIDER_GROUP" item-text="title"
                item-value="title" :loading="loadingProviderGroups" :disabled="isEditMode" :items="providerGroups"
                return-object @change="retrieveSecondaryOwners(selectedProviderGroup.id)">
                <template v-slot:no-data>
                  <v-list-tile>
                    <v-list-tile-title>
                      No Provider groups
                    </v-list-tile-title>
                  </v-list-tile>
                </template>
              </v-autocomplete>
            </v-flex>
            <!-- Provider -->
            <v-flex xs4>
              <v-autocomplete v-model="selectedSecondaryOwner" :label="messages.label.PROVIDER_NAME"
                :loading="loadingSecondaryOwner" item-text="title" item-value="title" return-object
                @change="updateAddRulesFilters" :items="secondaryOwners">
              </v-autocomplete>
            </v-flex>
          </div>
          <div v-else>
            <!-- Sub Account -->
            <v-flex sm4 v-if="mpxSubAccounts.length > 0">
              <v-autocomplete v-model="selectedSubAccount" :label="messages.label.SUB_ACCOUNT" item-text="title"
                item-value="title" :loading="loadingSubAccounts" :disabled="isEditMode" :items="mpxSubAccounts"
                return-object>
              </v-autocomplete>
            </v-flex>
            <!-- Sub Account Title-->
            <v-flex xs4 v-else>
              <v-text-field v-model="subAccountTitle" :clearable="!isEditMode" v-validate="'required|max:100'"
                :counter="100" :disabled="isEditMode" :label="messages.label.SUB_ACCOUNT" required>
              </v-text-field>
            </v-flex>
          </div>
        </template>
        <!-- Rule Set Type -->
        <v-flex xs4>
          <v-select v-model="ruleSetInfo.type" item-text="title" item-value="value" :disabled="isEditMode"
            @change="updateAddRulesFilters(); handleRuleSetTypeChange()" :label="messages.label.RULE_SET_TYPE"
            :items="ruleSetTypeList"></v-select>
        </v-flex>
        <!-- Rule Title -->
        <v-flex xs10>
          <v-text-field v-model="ruleSetInfo.title" clearable v-validate="'required|max:100'"
            :error-messages="errors.collect('title')" data-vv-name="title" :counter="100"
            :label="messages.label.RULE_TITLE" required>
          </v-text-field>
        </v-flex>
        <!-- Rule Description -->
        <v-flex xs10>
          <v-textarea v-model="ruleSetInfo.description" clearable v-validate="'max:100'"
            :error-messages="errors.collect('description')" data-vv-name="description" :counter="500"
            :label="messages.label.RULE_DESCRIPTION" required>
          </v-textarea>
        </v-flex>
        <!-- Rule Enabled -->
        <v-flex xs4>
          <span class="subheading">{{ messages.label.ENABLED }}</span>
          <v-switch v-model="ruleSetEnabled" color="primary" class="mt-0" :label="`${ruleSetEnabled.toString()}`">
          </v-switch>
        </v-flex>
      </v-form>
    </v-flex>
  </v-layout>

</template>
<script>
import { mapState, mapActions, mapGetters, mapMutations } from "vuex"
import messages from "@/MetadataRules/messages.json"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import { GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS } from "@/store/constants/action-types"
import { IS_POLARIS, VOD_SYCN_PREINGEST_DATA_SERVICE_URL } from "@/store/constants/getter-types"
import { SECONDARY_OWNER_ANY } from "@/Shared/constants"
import { SET_ADD_RULES_FILTERS, SET_RULESET_RULES } from "@/store/constants/mutation-types"

export default {
  name: "RuleSetInfo",
  $_veeValidate: {
    validator: "new"
  },
  computed: {
    ...mapState([
      "mpxParentAccount", "providerGroups", "mpxSubAccounts"
    ]),
    ...mapGetters([
      IS_POLARIS, VOD_SYCN_PREINGEST_DATA_SERVICE_URL
    ])
  },
  data() {
    return {
      ruleSetForList: MetadataRulesService.getForList(),
      ruleSetTypeList: MetadataRulesService.getRuleTypeList(),
      isEditMode: false,
      ruleSetInfo: {
        ruleSetFor: "global",
        type: "VALIDATION",
        ownerId: "",
        secondaryOwner: "",
        title: "",
        description: "",
        enabled: true
      },
      loadingProviderGroups: false,
      selectedProviderGroup: {},
      loadingSecondaryOwner: false,
      selectedSubAccount: {},
      subAccountTitle: "",
      loadingSubAccounts: false,
      secondaryOwners: [],
      selectedSecondaryOwner: {}, // providers are secondary owners
      ruleSetEnabled: true, // NOTE: Using this outside of 'ruleSetInfo' as binding is not working
      dictionary: this.getValidationDictionary()
    }
  },
  methods: {
    ...mapActions([
      GET_PROVIDER_GROUPS, GET_MPX_SUB_ACCOUNTS
    ]),
    ...mapMutations("metadataRules", [
      SET_ADD_RULES_FILTERS,
      SET_RULESET_RULES
    ]),

    /**
     * getValidationDictionary - Validation messages for vee-validate
     * @returns {object}
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 100 characters"
          },
          description: {
            max: "Description may not be greater than 500 characters"
          }
        }
      }
    },

    /**
     * resetData - Reset rule info data
     */
    resetData() {
      this.ruleSetInfo = {
        ruleSetFor: "global",
        type: "VALIDATION",
        ownerId: "",
        secondaryOwner: "",
        title: "",
        description: "",
        enabled: true
      }
      this.ruleSetEnabled = true
      this.isEditMode = false
    },

    /**
     * resetRuleSetInfo - Reset rule data and form
     */
    resetRuleSetInfo() {
      this.resetData()
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },

    /**
     * loadRuleSetInfo - update view with ruleset data
     * @param {object} ruleSetData
     */
    loadRuleSetInfo(ruleSetData) {
      this.resetRuleSetInfo()
      if (!this.$lodash.isUndefined(ruleSetData)) {
        if (this.IS_POLARIS) {
          if (this.isOwnerParent(ruleSetData.ownerId)) {
            this.ruleSetInfo.ruleSetFor = "global"
          } else {
            this.ruleSetInfo.ruleSetFor = "account"
            this.retrieveProviderGroups(ruleSetData.ownerId, ruleSetData.secondaryOwner)
          }
        } else {
          if (this.isOwnerParent(ruleSetData.ownerId)) {
            if (ruleSetData.secondaryOwner !== SECONDARY_OWNER_ANY) {
              this.ruleSetInfo.ruleSetFor = "account"
              this.subAccountTitle = ruleSetData.secondaryOwner
            } else {
              this.ruleSetInfo.ruleSetFor = "global"
            }
          } else {
            this.ruleSetInfo.ruleSetFor = "account"
            this.retrieveSubAccounts(ruleSetData.ownerId)
          }
        }

        this.isEditMode = true
        this.ruleSetInfo.type = ruleSetData.type
        this.ruleSetInfo.title = ruleSetData.title
        this.ruleSetInfo.ownerId = ruleSetData.ownerId
        this.ruleSetInfo.secondaryOwner = ruleSetData.secondaryOwner
        this.ruleSetInfo.description = ruleSetData.description
        this.ruleSetInfo.enabled = !ruleSetData.disabled
      } else {
        this.isEditMode = false
        this.retrieveProviderGroups()
      }
    },

    /**
     * isOwnerParent - Check if the rule's owner is parent account
     */
    isOwnerParent(ownerId) {
      return this.mpxParentAccount.id === ownerId
    },

    /**
     * retrieveProviderGroups - retrieve provider groups
     */
    retrieveProviderGroups(currentProviderGroupAccountId, currentSecondaryOwner) {
      // If the account is not Polaris, just return empty array
      if (!this.IS_POLARIS) {
        return
      }
      this.loadingProviderGroups = true
      this.GET_PROVIDER_GROUPS()
        .then(() => {
          if (!this.$lodash.isUndefined(currentProviderGroupAccountId)) {
            this.selectedProviderGroup = this.$lodash.find(this.providerGroups, { "ownerId": currentProviderGroupAccountId })
          } else {
            this.selectedProviderGroup = this.providerGroups[0]
          }
          return this.retrieveSecondaryOwners(this.selectedProviderGroup.id, currentSecondaryOwner)
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingProviderGroups = false
        })
    },

    /**
     * retrieveSubAccounts - retrieve provider groups
     */
    retrieveSubAccounts(currentSubAccountId) {
      this.loadingSubAccounts = true
      this.GET_MPX_SUB_ACCOUNTS()
        .then(() => {
          if (!this.$lodash.isUndefined(currentSubAccountId)) {
            this.selectedSubAccount = this.$lodash.find(this.mpxSubAccounts, { "id": currentSubAccountId })
          } else {
            this.selectedSubAccount = this.mpxSubAccounts[0]
          }
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingSubAccounts = false
        })
    },

    /**
     * retrieveSecondaryOwners - Retrieve all providers from API service
     * @param {string} selectedSecondaryOwners
     */
    retrieveSecondaryOwners(providerGroupId, currentSecondaryOwner) {
      this.updateAddRulesFilters()
      let queryObject = {}
      if (!this.$lodash.isUndefined(providerGroupId)) {
        queryObject["byProviderGroupId"] = providerGroupId
      }
      this.loadingSecondaryOwner = true
      return metadataRulesDataService
        .retrieveSecondaryOwners(queryObject,
          this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL)
        .then((secondaryOwners) => {
          this.secondaryOwners = secondaryOwners
          if (!this.$lodash.isUndefined(currentSecondaryOwner)) {
            if (currentSecondaryOwner === SECONDARY_OWNER_ANY) {
              this.selectedSecondaryOwner = this.secondaryOwners[0]
            } else {
              this.selectedSecondaryOwner = this.$lodash.find(this.secondaryOwners, { "title": currentSecondaryOwner })
            }
          } else {
            this.selectedSecondaryOwner = this.secondaryOwners[0]
          }
        })
        .catch(() => {
          // TODO: handle error
        }).finally(() => {
          this.loadingSecondaryOwner = false
        })
    },

    /**
     * validate - Validate
     */
    validate() {
      this.$validator.validateAll()
        .then((result) => {
          this.ruleSetInfo.enabled = this.ruleSetEnabled // Please see note at the definition of this property in 'data'
          if (this.$lodash.isNull(this.ruleSetInfo.description)) {
            this.ruleSetInfo.description = ""
          }
          // Update owner Id
          if (this.ruleSetInfo.ruleSetFor === "global") {
            this.ruleSetInfo.ownerId = this.mpxParentAccount.id
            this.ruleSetInfo.secondaryOwner = SECONDARY_OWNER_ANY
          } else {
            if (this.IS_POLARIS) {
              this.ruleSetInfo.ownerId = this.selectedProviderGroup.ownerId
              if (this.selectedSecondaryOwner.title === "Any") {
                this.ruleSetInfo.secondaryOwner = SECONDARY_OWNER_ANY
              } else {
                this.ruleSetInfo.secondaryOwner = this.selectedSecondaryOwner.title
              }
            } else {
              if (this.$lodash.isEmpty(this.selectedSubAccount)) {
                this.ruleSetInfo.ownerId = this.mpxParentAccount.id
                if (this.$lodash.isEmpty(this.subAccountTitle.trim())) {
                  this.ruleSetInfo.secondaryOwner = SECONDARY_OWNER_ANY
                } else {
                  this.ruleSetInfo.secondaryOwner = this.subAccountTitle
                }
              } else {
                this.ruleSetInfo.ownerId = this.selectedSubAccount.id
                this.ruleSetInfo.secondaryOwner = SECONDARY_OWNER_ANY
              }
            }
          }
          MetadataRulesService.$emit("event:ruleSetInfoValidation", result, this.ruleSetInfo)
        })
    },

    /**
     * updateAddRulesFilters - Update to ensure that 'Add Rules' fetches correct data
     */
    updateAddRulesFilters() {
      let addRulesFilters = {
        ruleSetFor: this.ruleSetInfo.ruleSetFor,
        ruleSetType: this.ruleSetInfo.type,
        providerGroupAccountId: this.selectedProviderGroup.ownerId
      }
      this.SET_ADD_RULES_FILTERS(addRulesFilters)
      MetadataRulesService.$emit("event:addRulesFilterUpdated")
    },
    /**
     * handleRuleSetTypeChange - Update the RulesSetRulesList when user change the ruleType
     */
    handleRuleSetTypeChange() {
      let ruleSetRulesList = []
      this.SET_RULESET_RULES(ruleSetRulesList)
      MetadataRulesService.$emit("event:ruleSetTypeChanged", this.ruleSetInfo.type)
    }
  },
  mounted() {
    // Validator initialization
    this.$validator.localize("en", this.dictionary)
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    this.updateAddRulesFilters()

    /**
     * Events
     */
    MetadataRulesService.$on("event:ruleSetDataRetrieved", this.loadRuleSetInfo)
    MetadataRulesService.$on("event:validateRuleSetInfo", this.validate)
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:validateRuleSetInfo", this.validate)
    MetadataRulesService.$off("event:ruleSetDataRetrieved", this.loadRuleSetInfo)
  }
}
</script>
<style lang="sass">

</style>



// WEBPACK FOOTER //
// src/MetadataRules/RuleSet/RuleSetInfo.vue